import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';

import { Popup } from '@/components';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { fulldate } from '@/utilities/dateUtils';

import './SysinfoPopup.css';
import { requestPlatformInformation } from '../flutter/DeviceInfo';

export default function SysinfoPopup({ closePopup }: { closePopup: () => Promise<void> }) {
  const { t } = useTranslation();
  const history: { who: string; when: string; what: string }[] = ENVIRONMENT !== 'stable' ? JSON.parse(HISTORY) : [];
  const [platformVersion, setPlatformVersion] = useState<string | undefined>(undefined);

  const isMobile = isPhoneOrHighScreen() ? t('error.dialog.acknowledgeSymbol') : 'X';

  const title = t('mainMenu.systemInformation');

  async function getPlatformInformation() {
    if (!window.platformInformation && !window.flutter_inappwebview) {
      return;
    }
    const platformInformation = await requestPlatformInformation();
    setPlatformVersion(platformInformation.app.version);
  }

  useEffect(() => {
    void getPlatformInformation();
  }, []);

  const body = (
    <div className="sysinfo-body">
      <div>
        <Typography variant="body2" className="sysinfo-block-title" data-qa-element-id="application_label">
          {t('systemInfo.application')}
        </Typography>
        <Paper className="sysinfo-box">
          <Box className="box">
            <div data-qa-element-id="systemInfo-buildtime" className="sysinfo">
              <Typography variant="subtitle1">{t('systemInfo.buildTime')}:</Typography>
              <Typography>{BUILD_TIMESTAMP}</Typography>
            </div>
            <div data-qa-element-id="systemInfo-version" className="sysinfo">
              <Typography variant="subtitle1">{t('systemInfo.version')}:</Typography>
              <Typography>
                {VERSION} {platformVersion && ' (' + platformVersion + ')'}
              </Typography>
            </div>
            <div data-qa-element-id="systemInfo-buildid" className="sysinfo">
              <Typography variant="subtitle1">{t('systemInfo.buildId')}:</Typography>
              <Typography>{BUILD_NUMBER}</Typography>
            </div>
          </Box>
        </Paper>
      </div>

      <div>
        <Typography variant="body2" className="sysinfo-block-title" data-qa-element-id="system_label">
          {t('systemInfo.system')}
        </Typography>
        <Paper className="sysinfo-box">
          <Box className="box">
            <div data-qa-element-id="systemInfo-platform" className="sysinfo">
              <Typography variant="subtitle1">{t('systemInfo.platform')}:</Typography>
              <Typography>{window.navigator.platform}</Typography>
            </div>
            <div data-qa-element-id="systemInfo-language" className="sysinfo">
              <Typography variant="subtitle1">{t('systemInfo.language')}:</Typography>
              <Typography>{window.navigator.language}</Typography>
            </div>
            <div data-qa-element-id="systemInfo-useragent" className="sysinfo">
              <Typography variant="subtitle1">{t('systemInfo.browser')}:</Typography>
              <Typography>{window.navigator.userAgent}</Typography>
            </div>
          </Box>
        </Paper>
      </div>

      <div>
        <Typography variant="body2" className="sysinfo-block-title" data-qa-element-id="device_label">
          {t('systemInfo.device')}
        </Typography>
        <Paper className="sysinfo-box">
          <Box className="box">
            <div data-qa-element-id="systemInfo-displaysize" className="sysinfo">
              <Typography variant="subtitle1">{t('systemInfo.displaySize')}:</Typography>
              <Typography>
                {window.screen.width} x {window.screen.height}
              </Typography>
            </div>
            <div data-qa-element-id="systemInfo-resolution" className="sysinfo">
              <Typography variant="subtitle1">{t('systemInfo.resolution')}:</Typography>
              <Typography>
                {Math.floor(window.screen.width * window.devicePixelRatio)} x{' '}
                {Math.floor(window.screen.height * window.devicePixelRatio)}
              </Typography>
            </div>
            <div data-qa-element-id="systemInfo-isphonescreen" className="sysinfo">
              <Typography variant="subtitle1">{t('systemInfo.phone')}:</Typography>
              <Typography>{isMobile}</Typography>
            </div>
          </Box>
        </Paper>
      </div>

      {ENVIRONMENT !== 'stable' && (
        <>
          <div>
            <Typography variant="body2" className="sysinfo-block-title" data-qa-element-id="developer_label">
              {t('systemInfo.development')}
            </Typography>
            <Paper className="sysinfo-box">
              <Box className="box">
                <div data-qa-element-id="systemInfo-builduser" className="sysinfo">
                  <Typography variant="subtitle1">{t('systemInfo.buildUser')}:</Typography>
                  <Typography>{USERNAME}</Typography>
                </div>
                <div data-qa-element-id="systemInfo-branchname" className="sysinfo">
                  <Typography variant="subtitle1">{t('systemInfo.branchName')}:</Typography>
                  <Typography>{BRANCHNAME}</Typography>
                </div>
                <div data-qa-element-id="systemInfo-environment" className="sysinfo">
                  <Typography variant="subtitle1">{t('systemInfo.environment')}:</Typography>
                  <Typography>{ENVIRONMENT}</Typography>
                </div>
                <div data-qa-element-id="systemInfo-last-commits" className="sysinfo">
                  <Typography variant="subtitle1">{t('systemInfo.lastCommits')}:</Typography>
                  <table data-qa-element-id="systemInfo-last-commits-table">
                    <tbody>
                      {history.map((h, i) => {
                        return (
                          <tr key={i}>
                            <td>{fulldate(new Date(h.when).getTime() * 1000, t)}</td>
                            <td>{h.who}</td>
                            <td style={{ wordBreak: 'break-word' }}>{h.what}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Box>
            </Paper>
          </div>
        </>
      )}
    </div>
  );

  if (isPhoneOrHighScreen()) {
    return (
      <Popup
        popupTitle={title}
        header={undefined}
        body={body}
        footer={undefined}
        closePopup={closePopup}
        isFullScr={true}
        showCloseButton={true}
        leftText={true}
      />
    );
  }

  return (
    <>
      <div className="sysinfo-container" data-qa-element-id="sysinfo_view">
        <div className="sysinfo-header">
          <Typography variant="h6" className="sysinfo-container-title" data-qa-element-id="sysinfo_view_title">
            {title}
          </Typography>
        </div>
        {body}
      </div>
    </>
  );
}
